/**
 * Wizard-2 one-time page: chrome, buybox-3 shell (buybox-2 engine), gifts, Shopify checkout.
 */
(function () {
  const HOST_ID = 'wizard-2-onetime-buybox-host';
  const WARNING_ICON =
    'https://cdn.shopify.com/s/files/1/0919/3534/2856/files/warning_status_color.svg?v=1768301061';

  function t(handler, key, fallback) {
    const v = handler.getLocaleString?.(key);
    if (v && String(v).trim() && !String(v).startsWith('quiz.steps.')) return String(v);
    return fallback;
  }

  function isNl(handler) {
    return handler.getWizard2PrimaryLocaleCode?.() === 'nl';
  }

  function quizContainer() {
    return document.querySelector('[data-quiz-container]');
  }

  function hostEl() {
    return document.getElementById(HOST_ID);
  }

  function totalBagsFromBuybox() {
    const flavors = window.Buybox2?.state?.flavors || {};
    let n = 0;
    Object.values(flavors).forEach((f) => {
      if (f && f.selected) n += Number(f.quantity) || 0;
    });
    return n;
  }

  function foodTotalCents() {
    const totals = window.Buybox2?.state?.totals;
    if (!totals) return 0;
    return Math.round(Number(totals.final || 0) * 100);
  }

  const Wizard2OnetimePage = {
    handler: null,
    _entered: false,

    hasPlanData(flow) {
      const state = flow?.state;
      if (!state) return false;
      const pet = state.getPetData?.() || {};
      const calcs = state.calculations || {};
      const full = state.selectedFlavorsFull || [];
      return !!(pet.name && calcs.dailyFoodG != null && full.length);
    },

    recommendedDistribution(handler) {
      return handler.getPlanBulkDistribution?.(1) || {};
    },

    applyRecommendedFlavors(handler) {
      const dist = this.recommendedDistribution(handler);
      const flavors = {
        rund: { quantity: 0, selected: false },
        kip: { quantity: 0, selected: false },
        vis: { quantity: 0, selected: false }
      };
      ['rund', 'kip', 'vis'].forEach((key) => {
        const qty = Math.max(0, Math.round(Number(dist[key]) || 0));
        flavors[key] = { quantity: qty, selected: qty > 0 };
      });
      if (window.Buybox2?.state) {
        window.Buybox2.state.flavors = flavors;
        window.Buybox2.state.deliveryMode = 'onetime';
        window.Buybox2.state.selectedBulkOption = 1;
        const first = ['rund', 'kip', 'vis'].find((k) => flavors[k].selected);
        if (first) window.Buybox2.state.activeFlavor = first;
      }
      return flavors;
    },

    enableBuyboxBridge(handler) {
      window.__wizard2OnetimeBuybox = {
        enabled: true,
        checkoutLabel: t(handler, 'quiz.steps.plan_overview.onetime_checkout', isNl(handler) ? 'Afrekenen' : 'Checkout'),
        getAllergyFlavorIds: () => {
          const pet = handler.flow.state.getPetData?.() || {};
          return handler.getAllergicFlavorIds?.(pet.allergies || []) || [];
        },
        allergyWarningText: () => {
          const pet = handler.flow.state.getPetData?.() || {};
          return String(t(handler, 'quiz.steps.flavors.allergy_warning', 'Contains allergens for %{pet_name}.')).replace(
            /%\{pet_name\}/g,
            pet.name || ''
          );
        },
        warningIconUrl: WARNING_ICON,
        onUpdate: () => this.syncFromBuybox(handler),
        checkout: (button) => this.checkout(handler, button)
      };
    },

    disableBuyboxBridge() {
      if (window.__wizard2OnetimeBuybox) window.__wizard2OnetimeBuybox.enabled = false;
    },

    formatMoney(handler, amount) {
      if (window.Buybox2?.formatPrice) return window.Buybox2.formatPrice(amount);
      const cents = Math.round(Number(amount || 0) * 100);
      if (typeof handler.formatMoney === 'function') {
        return handler.formatMoney(cents, handler.getWizard2ShippingMoneyForCountry?.()?.currency || 'EUR');
      }
      return String(amount);
    },

    async ensureFreebieCompare(handler) {
      if (this._freebieReady) return;
      const config = handler.flow.config || handler.flow.getConfig?.() || {};
      const state = handler.flow.state;
      const pct = config.transitionGuideAutomaticDiscountPercent ?? 100;
      const load = async (id, cacheKey) => {
        if (!id || !handler.flow.shopifyAPI?.getVariant) return 0;
        let variant = state[cacheKey];
        if (!variant) {
          try {
            variant = await handler.flow.shopifyAPI.getVariant(id);
            state[cacheKey] = variant;
          } catch (e) {
            return 0;
          }
        }
        return handler._wizard2FreebieOfferCents?.(variant, pct)?.compareCents || 0;
      };
      this._guideCompareCents = await load(config.freebieVariantId, '_wizard2TransitionGuideVariant');
      this._scoopCompareCents = await load(config.scoopFreebieVariantId, '_wizard2ScoopFreebieVariant');
      this._freebieReady = true;
    },

    checkoutTotals(handler) {
      const bags = totalBagsFromBuybox();
      const totals = window.Buybox2?.calculatePricingForMultiplier
        ? window.Buybox2.calculatePricingForMultiplier(1)
        : window.Buybox2?.state?.totals || { base: 0, final: 0 };
      const foodCompareCents = Math.round(Number(totals.base || 0) * 100);
      const foodFinalCents = Math.round(Number(totals.final || 0) * 100);
      const ship = handler.getWizard2ShippingMoneyForCountry?.() || {};
      const shipCents = Math.round(Number(ship.shipping || 0) * 100);
      const thresholdCents = Math.round(Number(ship.freeShippingThreshold || 70) * 100);
      const unlocked = bags > 0 && foodFinalCents >= thresholdCents;
      const guideCents = Number(this._guideCompareCents) || 0;
      const scoopCents = Number(this._scoopCompareCents) || 0;
      const compareCents = foodCompareCents + guideCents + scoopCents + shipCents;
      const finalCents = foodFinalCents + (unlocked ? 0 : shipCents);
      const savingsCents = Math.max(0, foodCompareCents + guideCents + scoopCents - foodFinalCents);
      return {
        bags,
        unlocked,
        compare: compareCents / 100,
        final: finalCents / 100,
        savings: savingsCents / 100
      };
    },

    syncFooter(handler) {
      const nextButton = document.querySelector('[data-next-button]');
      const labelEl = nextButton?.querySelector('.next-button-label');
      const priceWrap = document.querySelector('[data-onetime-next-price]');
      const compareEl = document.querySelector('[data-onetime-next-compare]');
      const finalEl = document.querySelector('[data-onetime-next-final]');
      const savingsWrap = document.querySelector('[data-wizard-2-onetime-savings]');
      const savingsText = document.querySelector('[data-wizard-2-onetime-savings-text]');
      const totals = this.checkoutTotals(handler);
      const isLoading = !!nextButton?.classList.contains('is-loading');

      if (labelEl && !isLoading) {
        labelEl.textContent = t(handler, 'quiz.steps.plan_overview.onetime_checkout', isNl(handler) ? 'Afrekenen' : 'Checkout');
      }
      if (nextButton && !isLoading) {
        nextButton.disabled = totals.bags === 0;
      }
      if (priceWrap) {
        if (totals.bags === 0) {
          priceWrap.hidden = true;
        } else {
          priceWrap.hidden = false;
          if (finalEl) finalEl.textContent = this.formatMoney(handler, totals.final);
          if (compareEl) {
            if (totals.compare > totals.final) {
              compareEl.style.display = '';
              compareEl.textContent = this.formatMoney(handler, totals.compare);
            } else {
              compareEl.style.display = 'none';
            }
          }
        }
      }
      if (savingsWrap && savingsText) {
        if (totals.bags === 0 || totals.savings <= 0) {
          savingsWrap.hidden = true;
        } else {
          savingsWrap.hidden = false;
          const amount = this.formatMoney(handler, totals.savings);
          const key = totals.unlocked
            ? 'quiz.steps.plan_overview.onetime_savings_shipping'
            : 'quiz.steps.plan_overview.onetime_savings';
          const fallback = totals.unlocked
            ? isNl(handler)
              ? 'Je bespaart %{amount} + gratis verzending'
              : 'You save %{amount} + free shipping'
            : isNl(handler)
              ? 'Je bespaart %{amount}'
              : 'You save %{amount}';
          savingsText.textContent = t(handler, key, fallback).replace('%{amount}', amount);
        }
      }
    },

    hideFooterExtras() {
      const savingsWrap = document.querySelector('[data-wizard-2-onetime-savings]');
      const priceWrap = document.querySelector('[data-onetime-next-price]');
      if (savingsWrap) savingsWrap.hidden = true;
      if (priceWrap) priceWrap.hidden = true;
    },

    moveBuybox(toSlot) {
      const shell = document.getElementById('buybox-3-inline');
      const inline = document.getElementById('buybox-2-inline');
      const popup = document.getElementById('buybox-2-popup');
      const host = hostEl();
      const slot = toSlot;
      const node = shell || inline;
      if (!node || !host) return;
      if (slot) {
        slot.appendChild(node);
        if (!shell && popup && popup.parentElement !== slot) slot.appendChild(popup);
      } else {
        host.appendChild(node);
        if (!shell && popup && popup.parentElement !== host) host.appendChild(popup);
      }
    },

    replaceStockCalculator(handler) {
      const calc = document.querySelector('#buybox-2-inline .buybox-2-stock-calculator');
      if (!calc) return;
      calc.innerHTML = '';
      const p = document.createElement('p');
      p.className = 'wizard-2-onetime-stock';
      p.setAttribute('data-wizard-2-onetime-stock', '');
      calc.appendChild(p);
      this.updateStockLine(handler);
    },

    updateStockLine(handler) {
      const el = document.querySelector('[data-wizard-2-onetime-stock]');
      if (!el) return;
      const pet = handler.flow.state.getPetData?.() || {};
      const calcs = handler.flow.state.calculations || {};
      const bags = totalBagsFromBuybox();
      const daysPerBag = Number(calcs.daysPerBag) || 0;
      const days = bags > 0 && daysPerBag > 0 ? Math.max(1, Math.round(bags * daysPerBag)) : 0;
      const bagWord =
        bags === 1
          ? t(handler, 'quiz.steps.plan_overview.onetime_bag_one', isNl(handler) ? 'zak' : 'bag')
          : t(handler, 'quiz.steps.plan_overview.onetime_bag_other', isNl(handler) ? 'zakken' : 'bags');
      const tpl = t(
        handler,
        'quiz.steps.plan_overview.onetime_stock',
        isNl(handler)
          ? '%{count} %{bag_word} is ongeveer %{days} dagen voorraad voor %{name}'
          : '%{count} %{bag_word} is about %{days} days of stock for %{name}'
      );
      el.textContent = tpl
        .replace('%{count}', String(bags))
        .replace('%{bag_word}', bagWord)
        .replace('%{days}', String(days))
        .replace('%{name}', pet.name || '');
    },

    bagsToUnlockShipping(handler) {
      const ship = handler.getWizard2ShippingMoneyForCountry?.() || {};
      const threshold = Number(ship.freeShippingThreshold) || 70;
      const thresholdCents = Math.round(threshold * 100);
      if (!window.Buybox2?.getCombinedDiscountPercent) {
        return Math.max(1, Math.ceil(threshold / 27.77));
      }
      const unit = Math.round((window.Buybox2.getBaseUnitPrice?.() || 27.77) * 100);
      if (unit <= 0) return 3;
      for (let n = 1; n <= 40; n++) {
        const pct = window.Buybox2.getCombinedDiscountPercent(n, 'onetime');
        const discounted = window.Buybox2.applyShopifyPercentDiscountCents
          ? window.Buybox2.discountedUnitCentsShopify(unit, 'onetime', n)
          : Math.max(0, unit - Math.floor((unit * pct) / 100));
        if (discounted * n >= thresholdCents) return n;
      }
      return 40;
    },

    updateGiftTiles(handler) {
      const shipTile = document.querySelector('[data-gift="shipping"]');
      const badge = document.querySelector('[data-wizard-2-onetime-shipping-badge]');
      if (!shipTile || !badge) return;
      const ship = handler.getWizard2ShippingMoneyForCountry?.() || {};
      const thresholdCents = Math.round(Number(ship.freeShippingThreshold || 70) * 100);
      const unlocked = foodTotalCents() >= thresholdCents && totalBagsFromBuybox() > 0;
      const bagsNeeded = this.bagsToUnlockShipping(handler);
      shipTile.classList.toggle('wizard-2-onetime-gift--locked', !unlocked);
      if (unlocked) {
        badge.textContent = t(handler, 'quiz.steps.plan_overview.onetime_gift_free', 'FREE');
      } else {
        const tpl = t(
          handler,
          'quiz.steps.plan_overview.onetime_gift_shipping_locked',
          isNl(handler) ? 'Vanaf %{count}+ zakken' : 'From %{count}+ bags'
        );
        badge.textContent = tpl.replace('%{count}', String(bagsNeeded));
      }
    },

    syncFromBuybox(handler) {
      this.updateStockLine(handler);
      this.updateGiftTiles(handler);
      this.syncFooter(handler);
    },

    applyPageChrome(on) {
      const el = quizContainer();
      if (el) el.classList.toggle('onetime-step-active', !!on);
      document.body.classList.toggle('wizard-2-onetime-page', !!on);
    },

    async enter(handler) {
      this.handler = handler;
      const root = handler.currentStepElement?.querySelector('[data-wizard-2-onetime]');
      if (!root) return;
      this.applyPageChrome(true);
      await handler.ensureWizard2ShippingRatesLoaded?.();
      await this.ensureFreebieCompare(handler);
      this.enableBuyboxBridge(handler);

      const slot = root.querySelector('[data-wizard-2-onetime-buybox-slot]');
      this.moveBuybox(slot);

      const cfg = window.BuyboxConfig2 || (window.BuyboxConfig2 = {});
      if (!cfg.productHandle) cfg.productHandle = 'forever-food';

      if (window.Buybox2 && typeof window.Buybox2.init === 'function') {
        this.applyRecommendedFlavors(handler);
        await window.Buybox2.init();
        if (typeof window.Buybox2.applyWizardOnetimeUi === 'function') {
          window.Buybox2.applyWizardOnetimeUi();
        }
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (typeof window.Buybox2.updatePriceBars === 'function') {
              window.Buybox2.updatePriceBars();
            }
          });
        });
      }

      this.replaceStockCalculator(handler);
      const gifts = root.querySelector('[data-wizard-2-onetime-gifts]');
      const stockWrap = document.querySelector('#buybox-2-inline .buybox-2-stock-calculator');
      if (gifts && stockWrap && stockWrap.parentElement) {
        stockWrap.parentElement.insertBefore(gifts, stockWrap.nextSibling);
      }
      this.syncFromBuybox(handler);
      this._entered = true;
      try {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      } catch (e) {
        window.scrollTo(0, 0);
      }
    },

    leave() {
      if (!this._entered && !document.querySelector('.onetime-step-active')) {
        this.hideFooterExtras();
        this.applyPageChrome(false);
        this.disableBuyboxBridge();
        return;
      }
      const gifts = document.querySelector('[data-wizard-2-onetime-gifts]');
      const page = document.querySelector('[data-wizard-2-onetime]');
      if (gifts && page && !page.contains(gifts)) {
        const guarantee = page.querySelector('.wizard-2-onetime-guarantee');
        if (guarantee) page.insertBefore(gifts, guarantee);
        else page.appendChild(gifts);
      }
      this.moveBuybox(null);
      if (typeof window.Buybox2?.resetPriceBarCurrentLabel === 'function') {
        window.Buybox2.resetPriceBarCurrentLabel();
      }
      this.hideFooterExtras();
      this.applyPageChrome(false);
      this.disableBuyboxBridge();
      this._entered = false;
    },

    async checkout(handler, button) {
      const state = handler.flow.state;
      const petData = state.getPetData();
      const config = typeof handler.flow.getConfig === 'function' ? handler.flow.getConfig() : handler.flow.config;
      const flavors = window.Buybox2?.state?.flavors || {};
      const variants = window.Buybox2?.PRODUCT?.variants || {};
      const visibleLabel =
        (window.__wizard2CartLinePropertyLabels && window.__wizard2CartLinePropertyLabels.one_time_for) ||
        (isNl(handler) ? 'Eenmalig voor' : 'One-time for');
      const petLineProps = handler.buildPetLineItemProperties(petData.name, visibleLabel);

      const cartItems = [];
      ['rund', 'kip', 'vis'].forEach((key) => {
        const fl = flavors[key];
        const qty = fl && fl.selected ? Math.max(0, Math.round(Number(fl.quantity) || 0)) : 0;
        if (!qty) return;
        const vid = parseInt(String(variants[key] || ''), 10);
        if (!vid) return;
        cartItems.push({
          variantId: vid,
          quantity: qty,
          properties: {
            ...petLineProps,
            _plan_type: 'onetime',
            _acquisition_flow: 'wizard-2',
            ...(state.petMetaobjectId ? { _pet_metaobject_id: String(state.petMetaobjectId) } : {})
          }
        });
      });
      if (!cartItems.length) return;

      if (button?.classList.contains('is-loading')) return;

      const reset = () => {
        if (!button) return;
        button.disabled = false;
        button.classList.remove('loading', 'is-loading');
        button.removeAttribute('aria-busy');
        this.syncFooter(handler);
      };

      button.disabled = true;
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');

      if (handler.flow.analytics?.trackEvent) {
        handler.flow.analytics.trackEvent('click_one-time', {
          pet_name: petData.name || null,
          flow: 'wizard-2'
        });
      }

      try {
        let existingCart = { items: [] };
        try {
          const r = await fetch('/cart.js');
          if (r.ok) existingCart = await r.json();
        } catch (e) {
          /* non-blocking */
        }

        // One-time: Transitiegids and scoop are true one-time gifts (no Digital plan).
        // Digital on the guide would be the only selling plan and show Recurring subtotal.
        const guideVariantId = config.freebieVariantId
          ? parseInt(String(config.freebieVariantId), 10)
          : 0;
        const checkoutFreebies = [
          { variantId: guideVariantId, properties: {} },
          {
            variantId: config.scoopFreebieVariantId
              ? parseInt(String(config.scoopFreebieVariantId), 10)
              : 0,
            properties: {}
          }
        ];
        const pendingPlanUpdates = [];
        for (const freebie of checkoutFreebies) {
          if (!freebie.variantId) continue;
          const existingLine = (existingCart.items || []).find(
            (it) => parseInt(String(it.variant_id), 10) === freebie.variantId
          );
          if (!existingLine) {
            cartItems.push({
              variantId: freebie.variantId,
              quantity: 1,
              properties: freebie.properties
            });
            continue;
          }
          const existingPlan = handler.wizard2CartLineSellingPlanId?.(existingLine) || '';
          if (!existingPlan || typeof handler.flow.shopifyAPI.changeCartLine !== 'function') continue;
          pendingPlanUpdates.push({
            key: existingLine.key,
            quantity: existingLine.quantity || 1,
            clearSellingPlan: true
          });
        }

        await handler.flow.shopifyAPI.addToCart(cartItems);
        for (const update of pendingPlanUpdates) {
          await handler.flow.shopifyAPI.changeCartLine(update);
        }

        if (typeof window.bzStampVwoExperiments === 'function') {
          try {
            await window.bzStampVwoExperiments();
          } catch (e) {}
        }

        try {
          const cartJsonAfterAdd = await fetch('/cart.js').then((r) => (r.ok ? r.json() : null));
          if (cartJsonAfterAdd?.items) {
            const petNamesSet = new Set();
            cartJsonAfterAdd.items.forEach((item) => {
              const petName = handler.getPetNameFromLineItemProperties(item.properties);
              if (petName) petNamesSet.add(petName);
            });
            const formattedNote = handler.formatCartNote(petData.ownerName, Array.from(petNamesSet));
            if (formattedNote) await handler.flow.shopifyAPI.updateCartNote(formattedNote);
          }
        } catch (e) {
          /* non-blocking */
        }

        const checkoutUrl =
          handler.buildCheckoutUrlWithPrefill?.(petData.ownerEmail, petData.ownerName) || '/checkout';
        const [checkoutUrlMain, checkoutUrlParams] = checkoutUrl.split('?');
        const newParams = checkoutUrlParams ? `skip_shop_pay=true&${checkoutUrlParams}` : 'skip_shop_pay=true';
        window.location.href = `${checkoutUrlMain}?${newParams}`;
      } catch (e) {
        console.error('Wizard-2 one-time checkout failed:', e);
        reset();
        if (window.bzToast) window.bzToast.showKey('cart_checkout_error');
      }
    }
  };

  window.Wizard2OnetimePage = Wizard2OnetimePage;
})();
